import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Module route map ──────────────────────────────────────────────────────────
const MODULE_ROUTE: Record<string, string> = {
  soc:          '/m/soc',
  video:        '/m/video',
  access:       '/m/access',
  incidents:    '/m/incidents',
  tours:        '/m/jobs',
  assets:       '/m/assets',
  events:       '/m/soc',
  reports:      '/m/reports',
  vision:       '/m/vision',
  carpark:      '/m/carpark',
  bookings:     '/m/carpark',
  integrations: '/m/integrations',
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface Module { id:string; label:string; icon:string; color:string; desc:string; licensed:boolean; }
interface Pane   { id:string; moduleId:string|null; }
interface PaneConfig { cameras?: string[]; }
interface LayoutPreset { id:string; label:string; svg:React.ReactNode; paneIds:string[]; gridCols:string; gridRows:string; areas:string[]; }

// ── Camera database (populated from Milestone XProtect in production) ─────────
export const ALL_CAMERAS = [
  { id:'TH-01', name:'Main Entry',          site:'Town Hall',          type:'PTZ',   status:'online',  res:'4K' },
  { id:'TH-02', name:'Reception',           site:'Town Hall',          type:'Fixed', status:'online',  res:'4MP' },
  { id:'TH-03', name:'Level 1 Corridor',    site:'Town Hall',          type:'Fixed', status:'online',  res:'4MP' },
  { id:'TH-04', name:'Level 2 Admin',       site:'Town Hall',          type:'Fixed', status:'online',  res:'4MP' },
  { id:'TH-05', name:'Council Chambers',    site:'Town Hall',          type:'Fixed', status:'online',  res:'4K' },
  { id:'TH-06', name:'Control Room',        site:'Town Hall',          type:'Fixed', status:'online',  res:'2MP' },
  { id:'CC-01', name:'Main Entry',          site:'Civic Centre',       type:'PTZ',   status:'online',  res:'4K' },
  { id:'CC-02', name:'Atrium',              site:'Civic Centre',       type:'PTZ',   status:'online',  res:'4K' },
  { id:'CC-03', name:'Level 1 Corridor',    site:'Civic Centre',       type:'Fixed', status:'online',  res:'4MP' },
  { id:'CC-04', name:'Rear Entry',          site:'Civic Centre',       type:'Fixed', status:'offline', res:'4MP' },
  { id:'CC-05', name:'Car Park Entry',      site:'Civic Centre',       type:'Fixed', status:'online',  res:'2MP' },
  { id:'AQ-01', name:'Main Pool',           site:'Aquatic Centre',     type:'Fixed', status:'online',  res:'4K' },
  { id:'AQ-02', name:'Entry',               site:'Aquatic Centre',     type:'Fixed', status:'online',  res:'4MP' },
  { id:'AQ-03', name:'Car Park',            site:'Aquatic Centre',     type:'PTZ',   status:'online',  res:'4MP' },
  { id:'AQ-04', name:'Change Rooms',        site:'Aquatic Centre',     type:'Fixed', status:'online',  res:'4MP' },
  { id:'DP-01', name:'Main Gate',           site:'Council Depot',      type:'PTZ',   status:'online',  res:'4K' },
  { id:'DP-02', name:'Vehicle Bay',         site:'Council Depot',      type:'Fixed', status:'online',  res:'4MP' },
  { id:'DP-03', name:'Workshop',            site:'Council Depot',      type:'Fixed', status:'online',  res:'2MP' },
  { id:'DP-04', name:'Storage Yard',        site:'Council Depot',      type:'PTZ',   status:'online',  res:'4MP' },
  { id:'PS-01', name:'North Plaza',         site:'Parramatta Square',  type:'PTZ',   status:'online',  res:'4K' },
  { id:'PS-02', name:'South Entry',         site:'Parramatta Square',  type:'PTZ',   status:'online',  res:'4K' },
  { id:'PS-03', name:'East Entrance',       site:'Parramatta Square',  type:'Fixed', status:'online',  res:'4MP' },
  { id:'PS-04', name:'West Entrance',       site:'Parramatta Square',  type:'Fixed', status:'online',  res:'4MP' },
  { id:'PS-05', name:'Undercover Area',     site:'Parramatta Square',  type:'Fixed', status:'online',  res:'2MP' },
  { id:'RW-01', name:'Path A',              site:'River Walk',         type:'Fixed', status:'online',  res:'4MP' },
  { id:'RW-02', name:'Amphitheatre',        site:'River Walk',         type:'PTZ',   status:'online',  res:'4K' },
  { id:'RW-03', name:'Playground Area',     site:'River Walk',         type:'Fixed', status:'online',  res:'2MP' },
];

const SITES_LIST = Array.from(new Set(ALL_CAMERAS.map(c => c.site)));

// ── Module Registry ───────────────────────────────────────────────────────────
const MODULES: Module[] = [
  { id:'soc',          label:'SOC Dashboard',  icon:'🛡️', color:'#ef4444', desc:'Alarm monitoring & response',  licensed:true },
  { id:'video',        label:'Video',           icon:'📹', color:'#3b82f6', desc:'Camera monitoring',            licensed:true },
  { id:'access',       label:'Access Control',  icon:'🔐', color:'#8b5cf6', desc:'Door events & users',          licensed:true },
  { id:'incidents',    label:'Incidents',       icon:'📋', color:'#f59e0b', desc:'Incident management',          licensed:true },
  { id:'tours',        label:'Guard Tours',     icon:'🚶', color:'#10b981', desc:'Patrol management',            licensed:true },
  { id:'assets',       label:'Assets',          icon:'🖥️', color:'#06b6d4', desc:'Asset lifecycle',              licensed:true },
  { id:'events',       label:'Events',          icon:'🎪', color:'#f97316', desc:'Event management',             licensed:true },
  { id:'reports',      label:'Reports',         icon:'📊', color:'#3b82f6', desc:'Analytics & reporting',        licensed:true },
  { id:'vision',       label:'Smart Vision',    icon:'👁️', color:'#a855f7', desc:'AI analytics',                licensed:false },
  { id:'carpark',      label:'Car Park',        icon:'🅿️', color:'#22c55e', desc:'Parking management',           licensed:false },
  { id:'bookings',     label:'Bookings',        icon:'📅', color:'#06b6d4', desc:'Facility booking',             licensed:false },
  { id:'integrations', label:'Integrations',    icon:'🔌', color:'#555555', desc:'System connections',           licensed:true },
];

// ── Layout Presets ────────────────────────────────────────────────────────────
const PRESETS: LayoutPreset[] = [
  { id:'single',       label:'Single',       svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="32" height="22" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/></svg>,                                                                                                                                                                                                                    paneIds:['a'],         gridCols:'1fr',     gridRows:'1fr',     areas:['1/1/2/2'] },
  { id:'side-by-side', label:'Side by Side', svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="14" height="22" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="20" y="2" width="14" height="22" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/></svg>,                                                                                paneIds:['a','b'],     gridCols:'1fr 1fr', gridRows:'1fr',     areas:['1/1/2/2','1/2/2/3'] },
  { id:'stacked',      label:'Stacked',      svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="32" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="2" y="15" width="32" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/></svg>,                                                                                  paneIds:['a','b'],     gridCols:'1fr',     gridRows:'1fr 1fr', areas:['1/1/2/2','2/1/3/2'] },
  { id:'main-panel',   label:'Main + Panel', svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="20" height="22" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="26" y="2" width="8" height="22" rx="2" fill="currentColor" fillOpacity="0.25" stroke="currentColor" strokeWidth="1.5"/></svg>,                                                                              paneIds:['a','b'],     gridCols:'2fr 1fr', gridRows:'1fr',     areas:['1/1/2/2','1/2/2/3'] },
  { id:'three-pane',   label:'Three Pane',   svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="18" height="22" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="24" y="2" width="10" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="24" y="15" width="10" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/></svg>, paneIds:['a','b','c'], gridCols:'2fr 1fr', gridRows:'1fr 1fr', areas:['1/1/3/2','1/2/2/3','2/2/3/3'] },
  { id:'quad',         label:'Quad',         svg:<svg width="36" height="26" viewBox="0 0 36 26"><rect x="2" y="2" width="14" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="20" y="2" width="14" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="2" y="15" width="14" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="20" y="15" width="14" height="9" rx="2" fill="currentColor" fillOpacity="0.5" stroke="currentColor" strokeWidth="1.5"/></svg>, paneIds:['a','b','c','d'], gridCols:'1fr 1fr', gridRows:'1fr 1fr', areas:['1/1/2/2','1/2/2/3','2/1/3/2','2/2/3/3'] },
];

// ── localStorage ──────────────────────────────────────────────────────────────
const LS_KEY = 'lemai_ws_v4';
function saveToStorage(uid:string, presetId:string, panes:Pane[], configs:Record<string,PaneConfig>) {
  try { localStorage.setItem(`${LS_KEY}_${uid}`, JSON.stringify({ presetId, panes, configs })); } catch {}
}
function loadFromStorage(uid:string):{presetId:string;panes:Pane[];configs:Record<string,PaneConfig>}|null {
  try { const r=localStorage.getItem(`${LS_KEY}_${uid}`); return r?JSON.parse(r):null; } catch { return null; }
}

// ── Camera Picker Modal ───────────────────────────────────────────────────────
function CameraPickerModal({ paneId, selected, onSave, onClose }: {
  paneId: string;
  selected: string[];
  onSave: (cameras: string[]) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string[]>(selected.slice());
  const [expandedSites, setExpandedSites] = useState<Record<string,boolean>>(
    SITES_LIST.reduce((a,s) => ({...a,[s]:true}), {} as Record<string,boolean>)
  );

  const filtered = search
    ? ALL_CAMERAS.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.site.toLowerCase().includes(search.toLowerCase()) ||
        c.id.toLowerCase().includes(search.toLowerCase())
      )
    : ALL_CAMERAS;

  const siteGroups = SITES_LIST.map(site => ({
    site,
    cameras: filtered.filter(c => c.site === site),
  })).filter(g => g.cameras.length > 0);

  function toggle(id: string) {
    setChosen(prev => prev.includes(id) ? prev.filter(x=>x!==id) : prev.concat(id));
  }

  function toggleSite(site: string, cams: typeof ALL_CAMERAS) {
    const ids = cams.map(c=>c.id);
    const allSelected = ids.every(id => chosen.includes(id));
    if (allSelected) {
      setChosen(prev => prev.filter(id => !ids.includes(id)));
    } else {
      const newOnes = ids.filter(id => !chosen.includes(id));
      setChosen(prev => prev.concat(newOnes));
    }
  }

  function toggleExpand(site: string) {
    setExpandedSites(prev => ({...prev, [site]: !prev[site]}));
  }

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div style={{
        background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)',
        borderRadius:'5px', width:'560px', maxHeight:'80vh',
        display:'flex', flexDirection:'column', overflow:'hidden',
        boxShadow:'0 24px 60px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.08)', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
            <div>
              <h3 style={{ margin:0, fontSize:'14px', fontWeight:700, color:'#e5e5e5' }}>Configure Camera View</h3>
              <div style={{ fontSize:'10px', color:'#555555', marginTop:'2px' }}>
                Select which cameras to display in this pane · {chosen.length} selected
              </div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:'#555555', cursor:'pointer', fontSize:'20px' }}>×</button>
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${ALL_CAMERAS.length} cameras by name or site...`}
            autoFocus
            style={{
              width:'100%', boxSizing:'border-box',
              background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
              borderRadius:'4px', padding:'8px 12px', color:'#e5e5e5', fontSize:'12px', outline:'none',
            }}
          />
          <div style={{ fontSize:'10px', color:'#444444', marginTop:'6px' }}>
            In production, all cameras from Milestone XProtect are listed here.
          </div>
        </div>

        {/* Camera list */}
        <div style={{ flex:1, overflowY:'auto', padding:'8px 0' }}>
          {chosen.length > 0 && (
            <div style={{ padding:'4px 20px 8px', borderBottom:'1px solid #1a1a1a' }}>
              <div style={{ fontSize:'9px', color:'#3b82f6', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'5px' }}>
                Selected ({chosen.length})
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                {chosen.map(id => {
                  const cam = ALL_CAMERAS.find(c=>c.id===id);
                  if (!cam) return null;
                  return (
                    <span key={id} style={{
                      display:'flex', alignItems:'center', gap:'4px',
                      fontSize:'10px', padding:'2px 8px', borderRadius:'4px',
                      background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', color:'#3b82f6',
                    }}>
                      {cam.id} — {cam.name}
                      <span onClick={() => toggle(id)} style={{ cursor:'pointer', opacity:0.7, marginLeft:'2px' }}>×</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {siteGroups.map(group => (
            <div key={group.site}>
              {/* Site header */}
              <div
                onClick={() => toggleExpand(group.site)}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'8px 20px', cursor:'pointer',
                  background:'rgba(255,255,255,0.015)',
                  borderBottom:'1px solid rgba(255,255,255,0.04)',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ fontSize:'11px', fontWeight:700, color:'#a0a0a0' }}>{group.site}</span>
                  <span style={{ fontSize:'9px', color:'#444444' }}>{group.cameras.length} cameras</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <button
                    onClick={e => { e.stopPropagation(); toggleSite(group.site, group.cameras); }}
                    style={{
                      fontSize:'9px', padding:'2px 8px', borderRadius:'6px',
                      background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)',
                      color:'#3b82f6', cursor:'pointer', fontWeight:600,
                    }}
                  >
                    {group.cameras.every(c => chosen.includes(c.id)) ? 'Deselect All' : 'Select All'}
                  </button>
                  <span style={{ fontSize:'10px', color:'#555555' }}>{expandedSites[group.site] ? '▴' : '▾'}</span>
                </div>
              </div>

              {/* Cameras */}
              {expandedSites[group.site] && group.cameras.map(cam => (
                <div
                  key={cam.id}
                  onClick={() => toggle(cam.id)}
                  style={{
                    display:'flex', alignItems:'center', gap:'12px',
                    padding:'8px 20px', cursor:'pointer',
                    background: chosen.includes(cam.id) ? 'rgba(59,130,246,0.06)' : 'transparent',
                    borderBottom:'1px solid rgba(255,255,255,0.03)',
                    transition:'background 0.1s',
                  }}
                >
                  {/* Checkbox */}
                  <div style={{
                    width:'16px', height:'16px', borderRadius:'4px', flexShrink:0,
                    border:`2px solid ${chosen.includes(cam.id)?'#3b82f6':'rgba(255,255,255,0.15)'}`,
                    background: chosen.includes(cam.id)?'#3b82f6':'transparent',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    {chosen.includes(cam.id) && <span style={{ color:'white', fontSize:'10px', fontWeight:700 }}>✓</span>}
                  </div>

                  {/* Camera info */}
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'12px', color: chosen.includes(cam.id)?'#e2e8f0':'#94a3b8', fontWeight:chosen.includes(cam.id)?600:400 }}>
                      {cam.name}
                    </div>
                    <div style={{ fontSize:'9px', color:'#555555' }}>{cam.id} · {cam.type} · {cam.res}</div>
                  </div>

                  {/* Status */}
                  <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                    <div style={{ width:'5px', height:'5px', borderRadius:'50%', background:cam.status==='online'?'#22c55e':'#ef4444' }}/>
                    <span style={{ fontSize:'9px', color:cam.status==='online'?'#22c55e':'#ef4444' }}>{cam.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.08)', display:'flex', gap:'8px', justifyContent:'flex-end', flexShrink:0 }}>
          <button onClick={() => setChosen([])} style={{ padding:'7px 14px', borderRadius:'4px', background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#555555', cursor:'pointer', fontSize:'11px' }}>
            Clear All
          </button>
          <button onClick={onClose} style={{ padding:'7px 14px', borderRadius:'4px', background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#555555', cursor:'pointer', fontSize:'11px' }}>
            Cancel
          </button>
          <button onClick={() => onSave(chosen)} style={{ padding:'7px 18px', borderRadius:'4px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', color:'white', cursor:'pointer', fontSize:'11px', fontWeight:700 }}>
            Apply ({chosen.length} cameras)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mini Module Previews ──────────────────────────────────────────────────────
interface MiniProps { config?: PaneConfig; }

function MiniSOC(_:MiniProps) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const t=setInterval(()=>setTick(x=>x+1),8000); return ()=>clearInterval(t); },[]);
  const alarms=[
    {id:1,type:'Motion Detected',site:'Town Hall Entry',time:'08:41',sev:'high'},
    {id:2,type:'Door Forced Open',site:'Civic Centre B2',time:'08:39',sev:'critical'},
    {id:3,type:'Duress Button',site:'Aquatic Centre',time:'08:35',sev:'critical'},
    {id:4,type:'Camera Tamper',site:'Council Depot',time:'08:21',sev:'medium'},
    {id:5,type:'Perimeter Breach',site:'Parramatta Square',time:'08:14',sev:'high'},
  ];
  const sc:Record<string,string>={critical:'#ef4444',high:'#f59e0b',medium:'#3b82f6'};
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'5px',marginBottom:'9px'}}>
        {[{l:'Active',v:'3',c:'#ef4444'},{l:"Ack'd",v:'12',c:'#f59e0b'},{l:'Sites',v:'12/12',c:'#22c55e'}].map(s=>(
          <div key={s.l} style={{background:'#111111',borderRadius:'7px',padding:'6px',textAlign:'center'}}>
            <div style={{fontSize:'16px',fontWeight:700,color:s.c}}>{s.v}</div>
            <div style={{fontSize:'9px',color:'#555555'}}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{fontSize:'9px',color:'#444444',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'5px'}}>Live Feed</div>
      {alarms.map(a=>(
        <div key={a.id} style={{display:'flex',alignItems:'center',gap:'7px',padding:'5px 7px',marginBottom:'3px',background:'#0d0d0d',borderRadius:'5px',borderLeft:`2px solid ${sc[a.sev]}`}}>
          <div style={{flex:1}}>
            <div style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500}}>{a.type}</div>
            <div style={{fontSize:'9px',color:'#555555'}}>{a.site}</div>
          </div>
          <div style={{fontSize:'9px',color:'#555555'}}>{a.time}</div>
        </div>
      ))}
    </div>
  );
}

function MiniVideo({ config }:MiniProps) {
  const selectedIds = config?.cameras;
  const displayCams = selectedIds && selectedIds.length > 0
    ? ALL_CAMERAS.filter(c => selectedIds.includes(c.id))
    : ALL_CAMERAS.slice(0, 8);
  return (
    <div style={{padding:'10px',height:'100%',overflow:'hidden',boxSizing:'border-box',display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'7px'}}>
        <span style={{fontSize:'9px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.08em'}}>
          {displayCams.length} Camera{displayCams.length!==1?'s':''}
        </span>
        <span style={{fontSize:'9px',color:'#22c55e',background:'rgba(34,197,94,0.1)',padding:'2px 6px',borderRadius:'4px'}}>
          {displayCams.filter(c=>c.status==='online').length}/{displayCams.length} Online
        </span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(displayCams.length<=4?2:4,4)},1fr)`,gap:'3px',flex:1}}>
        {displayCams.map(c=>(
          <div key={c.id} style={{background:'#080e1c',borderRadius:'3px',border:`1px solid ${c.status==='online'?'rgba(255,255,255,0.07)':'rgba(239,68,68,0.3)'}`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',position:'relative',overflow:'hidden',minHeight:'30px'}}>
            <div style={{position:'absolute',inset:0,backgroundImage:'linear-gradient(rgba(255,255,255,0.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.012) 1px,transparent 1px)',backgroundSize:'7px 5px'}}/>
            <div style={{fontSize:'5px',color:'#444444',position:'relative',zIndex:1,textAlign:'center',padding:'1px',lineHeight:1.2,overflow:'hidden'}}>{c.id}</div>
            <div style={{position:'absolute',top:'2px',right:'2px',width:'4px',height:'4px',borderRadius:'50%',background:c.status==='online'?'#22c55e':'#ef4444'}}/>
            {c.status==='online'&&<div style={{position:'absolute',top:'2px',left:'2px',fontSize:'4px',color:'#ef4444',fontWeight:700}}>LIVE</div>}
          </div>
        ))}
      </div>
      {(!selectedIds || selectedIds.length===0) && (
        <div style={{fontSize:'8px',color:'#111111',textAlign:'center',marginTop:'4px',fontStyle:'italic'}}>
          Click configure to select cameras
        </div>
      )}
    </div>
  );
}

function MiniAccess(_:MiniProps) {
  const evts=[{user:'S. Mitchell',door:'Main Entry',result:'granted',time:'08:41'},{user:'Unknown',door:'Server Room B2',result:'denied',time:'08:39'},{user:'J. Okafor',door:'Staff Gate',result:'granted',time:'08:35'},{user:'Admin Override',door:'Control Room',result:'granted',time:'08:21'},{user:'Unknown',door:'Depot Rear Gate',result:'denied',time:'07:58'}];
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'5px',marginBottom:'8px'}}>
        {[{l:'Granted',v:'42',c:'#22c55e'},{l:'Denied',v:'7',c:'#ef4444'}].map(s=>(
          <div key={s.l} style={{background:'#111111',borderRadius:'6px',padding:'6px',textAlign:'center'}}>
            <div style={{fontSize:'16px',fontWeight:700,color:s.c}}>{s.v}</div>
            <div style={{fontSize:'9px',color:'#555555'}}>{s.l}</div>
          </div>
        ))}
      </div>
      {evts.map((e,i)=>(
        <div key={i} style={{display:'flex',alignItems:'center',gap:'6px',padding:'4px 7px',marginBottom:'3px',borderRadius:'5px',background:'#0d0d0d',borderLeft:`2px solid ${e.result==='granted'?'#22c55e':'#ef4444'}`}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:'11px',color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.user}</div>
            <div style={{fontSize:'9px',color:'#555555'}}>{e.door}</div>
          </div>
          <div style={{fontSize:'9px',color:e.result==='granted'?'#22c55e':'#ef4444'}}>{e.result}</div>
        </div>
      ))}
    </div>
  );
}

function MiniIncidents(_:MiniProps) {
  const inc=[{title:'Suspicious Person',site:'Parramatta Square',pri:'high',status:'open'},{title:'Equipment Fault',site:'Civic Centre',pri:'medium',status:'investigating'},{title:'Vandalism Report',site:'River Walk',pri:'low',status:'open'}];
  const pc:Record<string,string>={high:'#ef4444',medium:'#f59e0b',low:'#555555'};
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'5px',marginBottom:'8px'}}>
        {[{l:'Open',v:'5',c:'#ef4444'},{l:'Active',v:'3',c:'#f59e0b'},{l:'Closed',v:'28',c:'#22c55e'}].map(s=>(
          <div key={s.l} style={{background:'#111111',borderRadius:'6px',padding:'5px',textAlign:'center'}}>
            <div style={{fontSize:'15px',fontWeight:700,color:s.c}}>{s.v}</div>
            <div style={{fontSize:'8px',color:'#555555'}}>{s.l}</div>
          </div>
        ))}
      </div>
      {inc.map((x,i)=>(
        <div key={i} style={{padding:'5px 7px',marginBottom:'3px',borderRadius:'5px',background:'#0d0d0d',borderLeft:`2px solid ${pc[x.pri]}`}}>
          <div style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500}}>{x.title}</div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:'1px'}}>
            <span style={{fontSize:'9px',color:'#555555'}}>{x.site}</span>
            <span style={{fontSize:'9px',color:pc[x.pri]}}>{x.pri}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniTours(_:MiniProps) {
  const tours=[{name:'Night Patrol',guard:'Officer Chen',pct:75,status:'in-progress'},{name:'Perimeter Check',guard:'Officer Davis',pct:100,status:'completed'},{name:'Basement Sweep',guard:'Officer Kim',pct:0,status:'scheduled'}];
  const sc:Record<string,string>={'in-progress':'#3b82f6',completed:'#22c55e',scheduled:'#555555'};
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {tours.map((t,i)=>(
        <div key={i} style={{marginBottom:'9px',padding:'7px',background:'#0d0d0d',borderRadius:'6px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'2px'}}>
            <span style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500}}>{t.name}</span>
            <span style={{fontSize:'9px',color:sc[t.status]}}>{t.status}</span>
          </div>
          <div style={{fontSize:'9px',color:'#555555',marginBottom:'4px'}}>{t.guard}</div>
          <div style={{height:'3px',background:'rgba(255,255,255,0.07)',borderRadius:'2px'}}>
            <div style={{height:'100%',width:`${t.pct}%`,background:sc[t.status],borderRadius:'2px'}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniAssets(_:MiniProps) {
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'5px',marginBottom:'8px'}}>
        {[{l:'Operational',v:'847',c:'#22c55e'},{l:'Maintenance',v:'23',c:'#f59e0b'},{l:'Faulty',v:'8',c:'#ef4444'}].map(s=>(
          <div key={s.l} style={{background:'#111111',borderRadius:'6px',padding:'5px',textAlign:'center'}}>
            <div style={{fontSize:'13px',fontWeight:700,color:s.c}}>{s.v}</div>
            <div style={{fontSize:'7px',color:'#555555',marginTop:'1px'}}>{s.l}</div>
          </div>
        ))}
      </div>
      {[{name:'IP Camera PTZ-001',type:'Camera',status:'maintenance'},{name:'Access Controller AC-04',type:'Controller',status:'faulty'},{name:'NVR Unit NVR-02',type:'NVR',status:'maintenance'}].map((a,i)=>(
        <div key={i} style={{padding:'4px 7px',marginBottom:'3px',borderRadius:'5px',background:'#0d0d0d'}}>
          <div style={{fontSize:'11px',color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:'1px'}}>
            <span style={{fontSize:'9px',color:'#555555'}}>{a.type}</span>
            <span style={{fontSize:'9px',color:a.status==='faulty'?'#ef4444':'#f59e0b'}}>{a.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniEvents(_:MiniProps) {
  const evts=[{name:'Parramatta Festival 2026',date:'Mar 15–16',att:'45,000',status:'confirmed'},{name:'Council AGM',date:'Apr 8',att:'200',status:'planning'},{name:'Saturday Markets',date:'Apr 12',att:'8,000',status:'confirmed'}];
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {evts.map((e,i)=>(
        <div key={i} style={{padding:'7px',marginBottom:'5px',background:'#0d0d0d',borderRadius:'6px',borderLeft:`2px solid ${e.status==='confirmed'?'#22c55e':'#f59e0b'}`}}>
          <div style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500}}>{e.name}</div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:'2px'}}>
            <span style={{fontSize:'9px',color:'#555555'}}>{e.date}</span>
            <span style={{fontSize:'9px',color:'#555555'}}>{e.att}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniReports(_:MiniProps) {
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {[{name:'Weekly Security Report',age:'2 hrs ago',type:'PDF'},{name:'Incident Summary',age:'1 day ago',type:'PDF'},{name:'Access Audit Log',age:'1 day ago',type:'CSV'},{name:'Camera Health Report',age:'2 days ago',type:'PDF'}].map((r,i)=>(
        <div key={i} style={{display:'flex',alignItems:'center',gap:'7px',padding:'5px 7px',marginBottom:'3px',background:'#0d0d0d',borderRadius:'5px'}}>
          <div style={{width:'22px',height:'22px',background:'rgba(59,130,246,0.12)',borderRadius:'4px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'6px',color:'#3b82f6',fontWeight:700,flexShrink:0}}>{r.type}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:'10px',color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name}</div>
            <div style={{fontSize:'8px',color:'#555555'}}>{r.age}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniVision(_:MiniProps) {
  return (
    <div style={{padding:'10px',height:'100%',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'7px'}}>
      <div style={{fontSize:'22px',opacity:0.2}}>👁️</div>
      <div style={{fontSize:'10px',color:'#555555',textAlign:'center',lineHeight:1.5}}>Smart Vision pending<br/>XProtect 2025 R2</div>
      <div style={{fontSize:'9px',color:'#3b82f6',background:'rgba(59,130,246,0.1)',padding:'2px 8px',borderRadius:'4px'}}>~6 weeks</div>
    </div>
  );
}

function MiniCarPark(_:MiniProps) {
  const zones=[{name:'P1 Staff',total:120,occ:87},{name:'P2 Visitors',total:80,occ:62},{name:'P3 Accessible',total:15,occ:4}];
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {zones.map((z,i)=>{const pct=Math.round((z.occ/z.total)*100);const c=pct>90?'#ef4444':pct>70?'#f59e0b':'#22c55e';return(
        <div key={i} style={{marginBottom:'9px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
            <span style={{fontSize:'11px',color:'#e5e5e5'}}>{z.name}</span>
            <span style={{fontSize:'10px',color:c,fontWeight:600}}>{z.occ}/{z.total}</span>
          </div>
          <div style={{height:'4px',background:'rgba(255,255,255,0.07)',borderRadius:'2px'}}>
            <div style={{height:'100%',width:`${pct}%`,background:c,borderRadius:'2px'}}/>
          </div>
        </div>
      );})}
    </div>
  );
}

function MiniBookings(_:MiniProps) {
  const bkgs=[{room:'Council Chamber',org:'City Planning',time:'09:00–11:00',status:'confirmed'},{room:'Meeting Room 3A',org:'HR Team',time:'11:30–12:30',status:'confirmed'},{room:'Training Room',org:'ICT Department',time:'14:00–17:00',status:'pending'}];
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {bkgs.map((b,i)=>(
        <div key={i} style={{padding:'5px 7px',marginBottom:'4px',background:'#0d0d0d',borderRadius:'5px',borderLeft:`2px solid ${b.status==='confirmed'?'#3b82f6':'#f59e0b'}`}}>
          <div style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500}}>{b.room}</div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:'1px'}}>
            <span style={{fontSize:'9px',color:'#555555'}}>{b.org}</span>
            <span style={{fontSize:'9px',color:'#555555'}}>{b.time}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniIntegrations(_:MiniProps) {
  const ints=[{name:'Milestone XProtect',status:'connected',ver:'2024 R1'},{name:'Protege GX',status:'pending',ver:'v3.4'},{name:'ArmorIP',status:'pending',ver:'—'},{name:'Azure AD',status:'connected',ver:'OAuth 2.0'}];
  return (
    <div style={{padding:'10px',height:'100%',overflow:'auto',boxSizing:'border-box'}}>
      {ints.map((x,i)=>(
        <div key={i} style={{display:'flex',alignItems:'center',gap:'7px',padding:'5px 7px',marginBottom:'3px',background:'#0d0d0d',borderRadius:'5px'}}>
          <div style={{width:'5px',height:'5px',borderRadius:'50%',background:x.status==='connected'?'#22c55e':'#f59e0b',flexShrink:0}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:'11px',color:'#e5e5e5'}}>{x.name}</div>
            <div style={{fontSize:'8px',color:'#555555'}}>{x.ver}</div>
          </div>
          <div style={{fontSize:'9px',color:x.status==='connected'?'#22c55e':'#f59e0b'}}>{x.status}</div>
        </div>
      ))}
    </div>
  );
}

const MINI: Record<string, React.FC<MiniProps>> = {
  soc:MiniSOC, video:MiniVideo, access:MiniAccess, incidents:MiniIncidents,
  tours:MiniTours, assets:MiniAssets, events:MiniEvents, reports:MiniReports,
  vision:MiniVision, carpark:MiniCarPark, bookings:MiniBookings, integrations:MiniIntegrations,
};

// Modules that support per-pane configuration
const CONFIGURABLE_MODULES = ['video'];

// ── Empty Pane ────────────────────────────────────────────────────────────────
function EmptyPane({ isOver }:{isOver:boolean}) {
  return (
    <div style={{height:'100%',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'10px',border:`1.5px dashed ${isOver?'#3b82f6':'rgba(255,255,255,0.08)'}`,borderRadius:'4px',background:isOver?'rgba(59,130,246,0.06)':'transparent',transition:'all 0.2s'}}>
      <div style={{fontSize:'20px',opacity:isOver?0.9:0.2}}>{isOver?'📌':'⊞'}</div>
      <div style={{fontSize:'11px',color:isOver?'#3b82f6':'#222222',fontWeight:500}}>{isOver?'Drop module here':'Drag a module here'}</div>
    </div>
  );
}

// ── Workspace Pane ────────────────────────────────────────────────────────────
function WorkspacePane({ pane, config, onRemove, onDrop, onConfigure, onLaunch, overPaneId, setOverPaneId }: {
  pane: Pane; config: PaneConfig;
  onRemove: (id:string) => void;
  onDrop: (paneId:string, moduleId:string) => void;
  onConfigure: (paneId:string) => void;
  onLaunch: (moduleId:string) => void;
  overPaneId: string|null;
  setOverPaneId: (id:string|null) => void;
}) {
  const mod = MODULES.find(m=>m.id===pane.moduleId);
  const Mini = pane.moduleId ? MINI[pane.moduleId] : null;
  const isOver = overPaneId===pane.id;
  const isConfigurable = pane.moduleId && CONFIGURABLE_MODULES.includes(pane.moduleId);
  const hasRoute = pane.moduleId && MODULE_ROUTE[pane.moduleId];

  function handleDragOver(e:React.DragEvent) { e.preventDefault(); e.dataTransfer.dropEffect='copy'; setOverPaneId(pane.id); }
  function handleDragLeave(e:React.DragEvent) { if(!e.currentTarget.contains(e.relatedTarget as Node)) setOverPaneId(null); }
  function handleDrop(e:React.DragEvent) { e.preventDefault(); setOverPaneId(null); const mId=e.dataTransfer.getData('moduleId'); if(mId) onDrop(pane.id,mId); }

  return (
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      style={{background:'#0d0d0d',border:`1px solid ${isOver?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.06)'}`,borderRadius:'4px',overflow:'hidden',display:'flex',flexDirection:'column',height:'100%',transition:'border-color 0.2s,box-shadow 0.2s',boxShadow:isOver?'0 0 0 2px rgba(59,130,246,0.2)':'none'}}>
      {mod && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 10px',background:'#0d0d0d',borderBottom:'1px solid rgba(255,255,255,0.04)',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <div style={{width:'20px',height:'20px',borderRadius:'4px',background:`${mod.color}18`,border:`1px solid ${mod.color}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px'}}>{mod.icon}</div>
            <span style={{fontSize:'10px',fontWeight:700,color:'#a0a0a0',textTransform:'uppercase',letterSpacing:'0.07em'}}>{mod.label}</span>
            <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'#22c55e',display:'inline-block',boxShadow:'0 0 4px #22c55e80'}}/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
            {isConfigurable && (
              <button onClick={()=>onConfigure(pane.id)} title="Configure cameras"
                style={{background:'rgba(255,255,255,0.05)',border:'1px solid #1a1a1a',borderRadius:'4px',color:'#555555',cursor:'pointer',fontSize:'11px',padding:'2px 6px',lineHeight:1,transition:'all 0.15s'}}
                onMouseOver={e=>{(e.currentTarget as HTMLButtonElement).style.color='#3b82f6';(e.currentTarget as HTMLButtonElement).style.borderColor='rgba(59,130,246,0.3)';}}
                onMouseOut={e=>{(e.currentTarget as HTMLButtonElement).style.color='#555555';(e.currentTarget as HTMLButtonElement).style.borderColor='rgba(255,255,255,0.08)';}}
              >
                {config.cameras && config.cameras.length > 0 ? `${config.cameras.length} cams ⚙` : '⚙ Cameras'}
              </button>
            )}
            {hasRoute && (
              <button
                onClick={() => onLaunch(pane.moduleId!)}
                title={`Open ${mod.label} in full view`}
                style={{background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.25)',borderRadius:'4px',color:'#3b82f6',cursor:'pointer',fontSize:'10px',fontWeight:700,padding:'2px 7px',lineHeight:1,transition:'all 0.15s',whiteSpace:'nowrap'}}
                onMouseOver={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(59,130,246,0.2)';}}
                onMouseOut={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(59,130,246,0.1)';}}
              >
                Open ↗
              </button>
            )}
            <button onClick={()=>onRemove(pane.id)} style={{background:'none',border:'none',color:'#555555',cursor:'pointer',fontSize:'15px',padding:'0 2px',lineHeight:1}}
              onMouseOver={e=>{(e.currentTarget as HTMLButtonElement).style.color='#94a3b8';}}
              onMouseOut={e=>{(e.currentTarget as HTMLButtonElement).style.color='#555555';}}
              title="Remove">×</button>
          </div>
        </div>
      )}
      <div
        style={{flex:1,overflow:'hidden',minHeight:0,position:'relative',cursor:hasRoute?'pointer':'default'}}
        onClick={hasRoute ? () => onLaunch(pane.moduleId!) : undefined}
        title={hasRoute ? `Click to open ${mod?.label}` : undefined}
      >
        {!mod ? <EmptyPane isOver={isOver}/> : Mini ? <Mini config={config}/> : null}
        {hasRoute && mod && (
          <div style={{
            position:'absolute',inset:0,
            background:'transparent',
            display:'flex',alignItems:'center',justifyContent:'center',
            opacity:0,transition:'opacity 0.2s',
          }}
            className="pane-launch-overlay"
          />
        )}
      </div>
    </div>
  );
}

// ── Module Library Card ───────────────────────────────────────────────────────
function ModuleCard({ mod }:{mod:Module}) {
  const [tip, setTip] = useState(false);
  function onDragStart(e:React.DragEvent) { if(!mod.licensed){e.preventDefault();return;} e.dataTransfer.setData('moduleId',mod.id); e.dataTransfer.effectAllowed='copy'; }
  return (
    <div draggable={mod.licensed} onDragStart={onDragStart} onMouseEnter={()=>setTip(true)} onMouseLeave={()=>setTip(false)}
      style={{position:'relative',display:'flex',alignItems:'center',gap:'8px',padding:'7px 9px',marginBottom:'3px',borderRadius:'4px',background:mod.licensed?'rgba(255,255,255,0.03)':'rgba(255,255,255,0.01)',border:`1px solid ${mod.licensed?'rgba(255,255,255,0.07)':'rgba(255,255,255,0.03)'}`,cursor:mod.licensed?'grab':'not-allowed',opacity:mod.licensed?1:0.4,filter:mod.licensed?'none':'grayscale(1)',transition:'all 0.15s',userSelect:'none'}}
      onMouseOver={e=>{if(mod.licensed)(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.06)';}}
      onMouseOut={e=>{if(mod.licensed)(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.03)';}}
    >
      <div style={{width:'24px',height:'24px',borderRadius:'5px',flexShrink:0,background:`${mod.color}20`,border:`1px solid ${mod.color}35`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px'}}>
        {mod.licensed?mod.icon:'🔒'}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:'11px',fontWeight:600,color:mod.licensed?'#cbd5e1':'#555555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{mod.label}</div>
        <div style={{fontSize:'8px',color:'#555555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{mod.desc}</div>
      </div>
      {tip&&!mod.licensed&&(
        <div style={{position:'absolute',left:'calc(100% + 8px)',top:'50%',transform:'translateY(-50%)',background:'#111111',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'7px',padding:'5px 10px',fontSize:'10px',color:'#e5e5e5',whiteSpace:'nowrap',zIndex:200,pointerEvents:'none',boxShadow:'0 4px 20px rgba(0,0,0,0.5)'}}>
          Upgrade to unlock
          <div style={{position:'absolute',right:'100%',top:'50%',transform:'translateY(-50%)',border:'5px solid transparent',borderRightColor:'#111111'}}/>
        </div>
      )}
    </div>
  );
}

// ── Main Workspace ────────────────────────────────────────────────────────────
export default function Workspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const uid = (user as any)?.id || 'default';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  const [time, setTime] = useState(new Date());
  const [savedMsg, setSavedMsg] = useState(false);
  const [overPaneId, setOverPaneId] = useState<string|null>(null);
  const [cameraPickerPaneId, setCameraPickerPaneId] = useState<string|null>(null);

  useEffect(() => { const t=setInterval(()=>setTime(new Date()),1000); return ()=>clearInterval(t); },[]);

  const getInitial = useCallback(() => {
    const saved = loadFromStorage(uid);
    if (saved) {
      const p = PRESETS.find(x=>x.id===saved.presetId)||PRESETS[1];
      return { presetId:p.id, panes:saved.panes as Pane[], configs: saved.configs||{} };
    }
    return { presetId:'side-by-side', panes:[{id:'a',moduleId:'soc'},{id:'b',moduleId:'video'}] as Pane[], configs:{} as Record<string,PaneConfig> };
  }, [uid]);

  const init = getInitial();
  const [presetId, setPresetId]   = useState(init.presetId);
  const [panes, setPanes]         = useState<Pane[]>(init.panes);
  const [paneConfigs, setPaneConfigs] = useState<Record<string,PaneConfig>>(init.configs);
  const preset = PRESETS.find(p=>p.id===presetId)||PRESETS[1];

  // Load layout from backend on mount (backend is authoritative; localStorage is fallback)
  useEffect(() => {
    if (!token) return;
    fetch('/api/workspace', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.layout) {
          const l = data.layout;
          const p = PRESETS.find(x=>x.id===l.presetId)||PRESETS[1];
          setPresetId(p.id);
          setPanes(l.panes as Pane[]);
          setPaneConfigs(l.configs||{});
          saveToStorage(uid, p.id, l.panes, l.configs||{});
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function switchPreset(newId:string) {
    const np = PRESETS.find(p=>p.id===newId)!;
    setPanes(np.paneIds.map((id,i)=>({id,moduleId:panes[i]?.moduleId||null})));
    setPresetId(newId);
  }
  function handleRemove(paneId:string) { setPanes(prev=>prev.map(p=>p.id===paneId?{...p,moduleId:null}:p)); }
  function handleDrop(paneId:string, moduleId:string) {
    const mod=MODULES.find(m=>m.id===moduleId);
    if(!mod?.licensed) return;
    setPanes(prev=>prev.map(p=>p.id===paneId?{...p,moduleId}:p));
  }
  function handleLaunch(moduleId:string) {
    const route = MODULE_ROUTE[moduleId];
    if (route) navigate(route);
  }
  function handleSave() {
    saveToStorage(uid, presetId, panes, paneConfigs);
    setSavedMsg(true);
    setTimeout(()=>setSavedMsg(false), 2000);
    // Persist to backend
    if (token) {
      fetch('/api/workspace', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ presetId, panes, configs: paneConfigs }),
      }).catch(() => {});
    }
  }
  function handleCameraPickerSave(cameras:string[]) {
    const paneId = cameraPickerPaneId!;
    setPaneConfigs(prev=>({...prev,[paneId]:{...prev[paneId],cameras}}));
    setCameraPickerPaneId(null);
  }

  const aestTime = time.toLocaleTimeString('en-AU',{timeZone:'Australia/Sydney',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false});
  const aestDate = time.toLocaleDateString('en-AU',{timeZone:'Australia/Sydney',weekday:'short',day:'numeric',month:'short'});

  return (
    <div style={{height:'100vh',display:'flex',flexDirection:'column',background:'#0a0a0a',color:'white',fontFamily:'system-ui,-apple-system,sans-serif',overflow:'hidden'}}>
      {/* Camera Picker Modal */}
      {cameraPickerPaneId && (
        <CameraPickerModal
          paneId={cameraPickerPaneId}
          selected={paneConfigs[cameraPickerPaneId]?.cameras || []}
          onSave={handleCameraPickerSave}
          onClose={() => setCameraPickerPaneId(null)}
        />
      )}

      {/* ── Top Bar ── */}
      <div style={{display:'flex',alignItems:'center',gap:'12px',padding:'0 16px',height:'50px',flexShrink:0,background:'rgba(255,255,255,0.015)',borderBottom:'1px solid #1a1a1a'}}>
        <button onClick={()=>navigate('/dashboard')} style={{background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',padding:0}}>
          <div style={{width:'26px',height:'26px',borderRadius:'6px',background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:700,color:'white'}}>L</div>
          <span style={{fontSize:'13px',fontWeight:700,color:'#e5e5e5',letterSpacing:'0.02em'}}>lemai</span>
        </button>
        <div style={{width:'1px',height:'18px',background:'rgba(255,255,255,0.07)'}}/>
        <div style={{display:'flex',alignItems:'center',gap:'2px'}}>
          {PRESETS.map(p=>(
            <button key={p.id} onClick={()=>switchPreset(p.id)} title={p.label}
              style={{background:p.id===presetId?'rgba(59,130,246,0.15)':'transparent',border:`1px solid ${p.id===presetId?'rgba(59,130,246,0.4)':'transparent'}`,borderRadius:'6px',cursor:'pointer',padding:'3px',color:p.id===presetId?'#3b82f6':'#555555',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}
              onMouseOver={e=>{if(p.id!==presetId)(e.currentTarget as HTMLButtonElement).style.color='#94a3b8';}}
              onMouseOut={e=>{if(p.id!==presetId)(e.currentTarget as HTMLButtonElement).style.color='#555555';}}
            >{p.svg}</button>
          ))}
        </div>
        <div style={{flex:1}}/>
        <span style={{fontSize:'10px',color:'#444444',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:600}}>Workspace</span>
        <div style={{width:'1px',height:'18px',background:'rgba(255,255,255,0.07)'}}/>
        <div style={{display:'flex',gap:'10px'}}>
          {[{c:'#ef4444',l:'3 Alarms'},{c:'#22c55e',l:'8/8 Online'}].map(s=>(
            <div key={s.l} style={{display:'flex',alignItems:'center',gap:'4px'}}>
              <span style={{width:'5px',height:'5px',borderRadius:'50%',background:s.c,display:'inline-block',boxShadow:`0 0 4px ${s.c}60`}}/>
              <span style={{fontSize:'10px',color:s.c}}>{s.l}</span>
            </div>
          ))}
        </div>
        <div style={{width:'1px',height:'18px',background:'rgba(255,255,255,0.07)'}}/>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:'12px',fontWeight:600,color:'#e5e5e5',fontVariantNumeric:'tabular-nums'}}>{aestTime}</div>
          <div style={{fontSize:'8px',color:'#444444'}}>{aestDate} AEST</div>
        </div>
        <div style={{width:'1px',height:'18px',background:'rgba(255,255,255,0.07)'}}/>
        <button onClick={handleSave}
          style={{background:savedMsg?'rgba(34,197,94,0.12)':'rgba(59,130,246,0.12)',border:`1px solid ${savedMsg?'rgba(34,197,94,0.3)':'rgba(59,130,246,0.3)'}`,borderRadius:'4px',color:savedMsg?'#22c55e':'#3b82f6',cursor:'pointer',fontSize:'11px',fontWeight:600,padding:'5px 14px',transition:'all 0.2s',whiteSpace:'nowrap'}}>
          {savedMsg?'Saved':'Save Layout'}
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{display:'flex',flex:1,overflow:'hidden',minHeight:0}}>
        {/* Module Library */}
        <div style={{width:'168px',flexShrink:0,background:'rgba(255,255,255,0.008)',borderRight:'1px solid rgba(255,255,255,0.06)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{padding:'10px 10px 7px',borderBottom:'1px solid rgba(255,255,255,0.04)',flexShrink:0}}>
            <div style={{fontSize:'9px',fontWeight:700,color:'#555555',textTransform:'uppercase',letterSpacing:'0.1em'}}>Module Library</div>
            <div style={{fontSize:'8px',color:'#555555',marginTop:'2px'}}>Drag to workspace</div>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'8px 8px 12px'}}>
            <div style={{fontSize:'8px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.09em',marginBottom:'5px',marginTop:'2px'}}>Licensed</div>
            {MODULES.filter(m=>m.licensed).map(m=><ModuleCard key={m.id} mod={m}/>)}
            <div style={{fontSize:'8px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.09em',marginBottom:'5px',marginTop:'12px'}}>Upgrade</div>
            {MODULES.filter(m=>!m.licensed).map(m=><ModuleCard key={m.id} mod={m}/>)}
          </div>
        </div>

        {/* Grid Area */}
        <div style={{flex:1,padding:'12px',overflow:'hidden',display:'flex',flexDirection:'column',minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px',flexShrink:0}}>
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <span style={{fontSize:'10px',color:'#555555'}}>Layout:</span>
              <span style={{fontSize:'10px',color:'#a0a0a0',fontWeight:600}}>{preset.label}</span>
              <span style={{fontSize:'9px',color:'#555555'}}>· {preset.paneIds.length} pane{preset.paneIds.length!==1?'s':''}</span>
            </div>
            <span style={{fontSize:'9px',color:'#555555'}}>{panes.filter(p=>p.moduleId).length}/{panes.length} active</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:preset.gridCols,gridTemplateRows:preset.gridRows,gap:'10px',flex:1,minHeight:0}}>
            {panes.map((pane,i)=>(
              <div key={pane.id} style={{gridArea:preset.areas[i]}}>
                <WorkspacePane
                  pane={pane} config={paneConfigs[pane.id]||{}}
                  onRemove={handleRemove} onDrop={handleDrop}
                  onConfigure={setCameraPickerPaneId}
                  onLaunch={handleLaunch}
                  overPaneId={overPaneId} setOverPaneId={setOverPaneId}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 16px',height:'26px',flexShrink:0,borderTop:'1px solid rgba(255,255,255,0.04)',background:'rgba(255,255,255,0.005)'}}>
        <span style={{fontSize:'9px',color:'#444444'}}>City of Parramatta Council · Lemai Intelligence Platform</span>
        <span style={{fontSize:'9px',color:'#444444'}}>v2.5.0 · {preset.label}</span>
      </div>
    </div>
  );
}
